import { format, isPast, isToday } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { useLearnerProgress, LearnerCertificate } from "@/hooks/useLearnerProgress";
import { Button } from "@/components/ui/button";
import { openCertificatePrintWindow } from "@/lib/certificateTemplate";
import { toast } from "sonner";
import { useLearnerActivity, ActivityItem } from "@/hooks/useLearnerActivity";
import { ExportLearnerCSV } from "@/components/admin/learning/ExportLearnerCSV";
import {
  GraduationCap,
  BookOpen,
  Award,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Calendar,
  User,
  Layers,
  FileText,
  MessageSquare,
  Trophy,
  Play,
  Printer,
} from "lucide-react";

interface LearnerProgressPanelProps {
  learnerId: string;
}

export function LearnerProgressPanel({ learnerId }: LearnerProgressPanelProps) {
  const { data, isLoading } = useLearnerProgress(learnerId);
  const { data: activities, isLoading: activitiesLoading } = useLearnerActivity(learnerId);

  /**
   * Opens the member's certificate in a print window on staff's behalf.
   *
   * Reuses the same template and print path members get from their own
   * profile, so a staff-printed certificate is byte-identical to a
   * self-printed one. Reading another member's certificate is already
   * permitted by RLS for admins, and this panel is admin-only.
   */
  const handlePrintCertificate = (certificate: LearnerCertificate) => {
    const opened = openCertificatePrintWindow({
      userName: certificate.userName,
      courseName: certificate.curriculumTitle,
      certificateNumber: certificate.certificateNumber,
      issuedAt: certificate.issuedAt,
    });

    if (!opened) {
      toast.error("Your browser blocked the certificate window. Please allow pop-ups and try again.");
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-12 w-12 rounded-full" />
          <div className="space-y-2">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-4 w-60" />
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!data?.profile) {
    return (
      <Card className="p-6">
        <p className="text-muted-foreground text-center">Learner not found.</p>
      </Card>
    );
  }

  const getStatusIcon = (isCompleted: boolean, progressPercentage: number) => {
    if (isCompleted) return <CheckCircle2 className="h-4 w-4 text-green-600" />;
    if (progressPercentage > 0) return <Play className="h-4 w-4 text-blue-600" />;
    return <Clock className="h-4 w-4 text-muted-foreground" />;
  };

  const getStatusLabel = (isCompleted: boolean, progressPercentage: number) => {
    if (isCompleted) return "Completed";
    if (progressPercentage > 0) return "In Progress";
    return "Not Started";
  };

  const isOverdue = (dueDate: string | null, completedAt: string | null) => {
    if (!dueDate || completedAt) return false;
    return isPast(new Date(dueDate)) && !isToday(new Date(dueDate));
  };

  const getActivityIcon = (type: ActivityItem["type"]) => {
    switch (type) {
      case "module_completed":
        return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      case "quiz_attempt":
        return <FileText className="h-4 w-4 text-blue-600" />;
      case "curriculum_enrolled":
        return <Layers className="h-4 w-4 text-purple-600" />;
      case "course_enrolled":
        return <BookOpen className="h-4 w-4 text-indigo-600" />;
      case "certificate_earned":
        return <Award className="h-4 w-4 text-amber-600" />;
      case "discussion_created":
      case "discussion_reply":
        return <MessageSquare className="h-4 w-4 text-teal-600" />;
      default:
        return <Clock className="h-4 w-4 text-muted-foreground" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 bg-muted/50 rounded-lg">
        <div className="flex items-center gap-4">
          <UserAvatar
            avatarPath={data.profile.avatarUrl}
            fullName={data.profile.fullName}
            className="h-12 w-12"
            thumbnailSize={64}
          />
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold">{data.profile.fullName || "Unknown"}</h3>
              <Badge variant="secondary" className="capitalize">
                {data.profile.role || "User"}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">{data.profile.email || "No email"}</p>
          </div>
        </div>
        <ExportLearnerCSV 
          learnerId={learnerId} 
          learnerName={data.profile.fullName}
        />
      </div>

      {/* Summary Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Enrollments</CardTitle>
            <GraduationCap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {data.summary.totalCurriculumEnrollments + data.summary.totalCourseEnrollments}
            </div>
            <p className="text-xs text-muted-foreground">
              {data.summary.totalCurriculumEnrollments} curricula, {data.summary.totalCourseEnrollments} courses
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completed</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {data.summary.completedCurriculumEnrollments + data.summary.completedCourseEnrollments}
            </div>
            <p className="text-xs text-muted-foreground">
              {data.summary.completedCurriculumEnrollments} curricula, {data.summary.completedCourseEnrollments} courses
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Certificates Earned</CardTitle>
            <Award className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.summary.certificatesEarned}</div>
            <p className="text-xs text-muted-foreground">
              Avg progress: {data.summary.averageCurriculumProgress}%
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Curriculum Progress */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Layers className="h-4 w-4" />
            Curriculum Progress
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.curriculumEnrollments.length === 0 ? (
            <p className="text-sm text-muted-foreground">No curriculum enrollments.</p>
          ) : (
            <div className="space-y-4">
              {data.curriculumEnrollments.map((enrollment) => {
                const certificate = data.certificates.find(
                  (c) => c.curriculumId === enrollment.curriculumId
                );
                return (
                  <div key={enrollment.id} className="border rounded-lg p-4 space-y-3">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(enrollment.isCompleted, enrollment.progressPercentage)}
                        <span className="font-medium">{enrollment.curriculumTitle}</span>
                        {certificate && (
                          <>
                            <Badge variant="default" className="bg-amber-500 hover:bg-amber-600">
                              <Trophy className="h-3 w-3 mr-1" />
                              Certified
                            </Badge>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 px-2"
                              onClick={() => handlePrintCertificate(certificate)}
                              title={`Certificate ${certificate.certificateNumber}`}
                            >
                              <Printer className="h-3.5 w-3.5 mr-1.5" />
                              Print certificate
                            </Button>
                          </>
                        )}
                      </div>
                      <Badge
                        variant={enrollment.isCompleted ? "default" : "secondary"}
                        className={enrollment.isCompleted ? "bg-green-600 hover:bg-green-700" : ""}
                      >
                        {getStatusLabel(enrollment.isCompleted, enrollment.progressPercentage)}
                      </Badge>
                    </div>
                    <div className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span>Progress</span>
                        <span className="text-muted-foreground">
                          {enrollment.completedCourses} / {enrollment.totalCourses} courses ({enrollment.progressPercentage}%)
                        </span>
                      </div>
                      <Progress value={enrollment.progressPercentage} className="h-2" />
                    </div>
                    <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        Enrolled: {format(new Date(enrollment.enrolledAt), "MMM d, yyyy")}
                      </span>
                      {enrollment.completedAt && (
                        <span className="flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3" />
                          Completed: {format(new Date(enrollment.completedAt), "MMM d, yyyy")}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Course Progress */}
      {data.courseEnrollments.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              Course Progress
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {data.courseEnrollments.map((enrollment) => (
                <div key={enrollment.id} className="border rounded-lg p-4 space-y-3">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(enrollment.isCompleted, enrollment.progressPercentage)}
                      <span className="font-medium">{enrollment.courseTitle}</span>
                      {enrollment.curriculumTitle && (
                        <span className="text-xs text-muted-foreground">
                          ({enrollment.curriculumTitle})
                        </span>
                      )}
                    </div>
                    <Badge
                      variant={enrollment.isCompleted ? "default" : "secondary"}
                      className={enrollment.isCompleted ? "bg-green-600 hover:bg-green-700" : ""}
                    >
                      {getStatusLabel(enrollment.isCompleted, enrollment.progressPercentage)}
                    </Badge>
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>Progress</span>
                      <span className="text-muted-foreground">
                        {enrollment.completedModules} / {enrollment.totalModules} modules ({enrollment.progressPercentage}%)
                      </span>
                    </div>
                    <Progress value={enrollment.progressPercentage} className="h-2" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Assignments */}
      {data.assignments.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Assignments ({data.assignments.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.assignments.map((assignment) => {
                const overdue = isOverdue(assignment.dueDate, assignment.completedAt);
                return (
                  <div
                    key={assignment.id}
                    className={`flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 border rounded-lg ${
                      overdue ? "border-destructive/50 bg-destructive/5" : ""
                    }`}
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="capitalize text-xs">
                          {assignment.type}
                        </Badge>
                        <span className="font-medium">{assignment.itemTitle}</span>
                        {assignment.required && (
                          <Badge variant="secondary" className="text-xs">
                            Required
                          </Badge>
                        )}
                        {overdue && (
                          <Badge variant="destructive" className="text-xs">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            Overdue
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                        {assignment.assignedByName && (
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            Assigned by: {assignment.assignedByName}
                          </span>
                        )}
                        {assignment.dueDate && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Due: {format(new Date(assignment.dueDate), "MMM d, yyyy")}
                          </span>
                        )}
                      </div>
                    </div>
                    <Badge
                      variant={assignment.completedAt ? "default" : assignment.acknowledgedAt ? "secondary" : "outline"}
                      className={assignment.completedAt ? "bg-green-600 hover:bg-green-700" : ""}
                    >
                      {assignment.completedAt
                        ? "Completed"
                        : assignment.acknowledgedAt
                        ? "Acknowledged"
                        : "Pending"}
                    </Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Activity Timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          {activitiesLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-start gap-3">
                  <Skeleton className="h-8 w-8 rounded-full" />
                  <div className="space-y-1 flex-1">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : !activities || activities.length === 0 ? (
            <p className="text-sm text-muted-foreground">No recent activity.</p>
          ) : (
            <div className="space-y-4">
              {activities.map((activity, index) => (
                <div key={`${activity.type}-${activity.timestamp}-${index}`} className="flex items-start gap-3">
                  <div className="mt-0.5 p-1.5 rounded-full bg-muted">
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="flex-1 space-y-1">
                    <p className="text-sm">{activity.description}</p>
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(activity.timestamp), "MMM d, yyyy 'at' h:mm a")}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
